import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, DatePicker, Empty, Space, Input, Tooltip, Tag } from 'antd';
import {
  CalculatorOutlined, SearchOutlined,
  CheckCircleFilled, WarningFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatMontant } from '../../variables/constants';
import { GREEN, ROUGE, Montant, TagTypeCompte, LIBELLE_TYPE } from './commun';
import TableExportButton from '../../components/TableExportButton';

const { RangePicker } = DatePicker;

/* La balance arrive en arbre : un compte de regroupement (4111, 4511, 4711…)
   porte le CUMUL de ses auxiliaires, qui pendent sous lui. On retire les
   tableaux `enfants` vides, sinon antd dessine un chevron sur les comptes
   ordinaires, qui ne s'ouvre sur rien. */
const nettoyer = (l) => {
  const enfants = (l.enfants || []).map(nettoyer);
  return enfants.length ? { ...l, enfants } : { ...l, enfants: undefined };
};

/* Un regroupement reste visible dès qu'un de ses auxiliaires correspond : chercher
   « FIDELIA » doit montrer 411101 SOUS son collectif, pas l'en arracher. */
const filtrer = (l, q) => {
  const correspond = `${l.numeroCompte} ${l.libelleCompte}`.toLowerCase().includes(q);
  const enfants = (l.enfants || []).map((e) => filtrer(e, q)).filter(Boolean);
  if (!correspond && !enfants.length) return null;
  // Un collectif retenu pour lui-même garde toute sa descendance : son montant
  // est un cumul, l'amputer d'une partie de ses enfants le rendrait inexplicable.
  return { ...l, enfants: correspond ? l.enfants : enfants };
};

/* Un export à plat : le tableur n'a pas de lignes repliables. Les auxiliaires
   suivent leur collectif, décalés par leur numéro, et la colonne « Nature de la
   ligne » dit lesquelles sont des cumuls — sans quoi additionner la colonne dans
   Excel donnerait le double du vrai total. */
const aplatir = (liste, sortie = []) => {
  liste.forEach((l) => {
    sortie.push(l);
    if (l.enfants?.length) aplatir(l.enfants, sortie);
  });
  return sortie;
};

/**
 * Balance des comptes — six colonnes : solde au début de la période, mouvements
 * de la période, solde à la fin.
 *
 * Toutes les écritures y entrent, annulations comprises : une annulation reprend
 * l'écriture d'origine à l'identique avec les montants en négatif, sans effacer
 * l'originale — les deux colonnes diminuent d'autant et le compte revient à zéro.
 */
export default function Balance() {
  // `tr` et non `t` : `t` désigne déjà les totaux de la balance, plus bas.
  const { t: tr } = useTranslation('comptabilite');
  const [periode, setPeriode] = useState([dayjs().startOf('year'), dayjs()]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recherche, setRecherche] = useState('');

  const charger = useCallback(async () => {
    const debut = periode?.[0]?.format('YYYY-MM-DD');
    const fin = periode?.[1]?.format('YYYY-MM-DD');
    if (!debut || !fin) {
      toast.info(tr('balance.choisirPeriode'));
      return;
    }
    setLoading(true);
    try {
      const r = await axiosInstance.get('/v1/comptabilite/balance', { params: { debut, fin } });
      setBalance(r.data?.data || null);
    } catch (e) {
      setBalance(null);
      toast.error(e?.response?.data?.message || tr('balance.calculImpossible'));
    } finally {
      setLoading(false);
    }
  }, [periode, tr]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, []);

  const lignes = useMemo(() => {
    const toutes = (balance?.lignes || []).map(nettoyer);
    const q = recherche.trim().toLowerCase();
    if (!q) return toutes;
    return toutes.map((l) => filtrer(l, q)).filter(Boolean);
  }, [balance, recherche]);

  const lignesAPlat = useMemo(() => aplatir(lignes), [lignes]);

  const t = balance?.totaux;
  const libellePeriode = balance
    ? tr('balance.periode', { debut: dayjs(balance.debut).format('DD/MM/YYYY'), fin: dayjs(balance.fin).format('DD/MM/YYYY') })
    : undefined;

  const colonneMontant = (titre, cle) => ({
    title: titre, dataIndex: cle, key: cle, width: 135, align: 'right',
    format: 'currency',
    render: (v) => <Montant valeur={v} />,
  });

  const colonnes = [
    { title: tr('balance.colonnes.compte'), dataIndex: 'numeroCompte', key: 'numeroCompte', width: 100, fixed: 'left',
      render: (v) => <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</span> },
    { title: tr('balance.colonnes.intitule'), dataIndex: 'libelleCompte', key: 'libelleCompte', ellipsis: true,
      render: (v, r) => (
        <Space size={8}>
          <span style={r.regroupement ? { fontWeight: 600 } : undefined}>{v}</span>
          <TagTypeCompte type={r.typeCompte} />
        {r.regroupement && (
          <Tooltip title={tr('balance.regroupement.aide')}>
            <Tag color="green" style={{ marginInlineEnd: 0, fontWeight: 500 }}>{tr('balance.regroupement.etiquette')}</Tag>
          </Tooltip>
        )}
        </Space>
      ),
      export: (v) => v },
    { title: tr('balance.colonnes.soldeDebut'), children: [
      colonneMontant(tr('balance.colonnes.debit'), 'soldeDebutDebiteur'),
      colonneMontant(tr('balance.colonnes.credit'), 'soldeDebutCrediteur'),
    ] },
    { title: tr('balance.colonnes.mouvements'), children: [
      colonneMontant(tr('balance.colonnes.debit'), 'mouvementDebit'),
      colonneMontant(tr('balance.colonnes.credit'), 'mouvementCredit'),
    ] },
    { title: tr('balance.colonnes.soldeFin'), children: [
      colonneMontant(tr('balance.colonnes.debit'), 'soldeFinDebiteur'),
      colonneMontant(tr('balance.colonnes.credit'), 'soldeFinCrediteur'),
    ] },
  ];

  /* Les exports ne connaissent pas les colonnes groupées : on les aplatit en
     préfixant le titre, pour que « Débit » ne soit pas ambigu dans le fichier. */
  const colonnesExport = [
    { title: tr('balance.colonnes.compte'), dataIndex: 'numeroCompte' },
    { title: tr('balance.colonnes.intitule'), dataIndex: 'libelleCompte' },
    { title: tr('balance.export.nature'), dataIndex: 'typeCompte', export: (v) => LIBELLE_TYPE[v] || v },
    { title: tr('balance.export.natureLigne'), dataIndex: 'regroupement',
      export: (v) => (v ? tr('balance.export.ligneCumul') : tr('balance.export.ligneCompte')) },
    { title: tr('balance.export.soldeDebutDebit'), dataIndex: 'soldeDebutDebiteur', format: 'currency' },
    { title: tr('balance.export.soldeDebutCredit'), dataIndex: 'soldeDebutCrediteur', format: 'currency' },
    { title: tr('balance.export.mouvementsDebit'), dataIndex: 'mouvementDebit', format: 'currency' },
    { title: tr('balance.export.mouvementsCredit'), dataIndex: 'mouvementCredit', format: 'currency' },
    { title: tr('balance.export.soldeFinDebit'), dataIndex: 'soldeFinDebiteur', format: 'currency' },
    { title: tr('balance.export.soldeFinCredit'), dataIndex: 'soldeFinCrediteur', format: 'currency' },
  ];

  return (
    <Layout title={tr('balance.titreCourt')}>
      {/* Fond vert pâle pour distinguer un cumul d'un compte ordinaire. Pas de
          filet coloré à gauche : la teinte suffit et ne hache pas le tableau. */}
      <style>{`.ligne-regroupement > td { background: #f2f8f4; }
               .ligne-regroupement:hover > td { background: #e8f2ec !important; }`}</style>
      <PageHeader
        titre={tr('balance.titre')}
        icone={<CalculatorOutlined />}
        compte={balance ? lignesAPlat.length : null}
        unite={tr('balance.unite')}
        unitePluriel={tr('balance.unitePluriel')}
        precision={libellePeriode}
      />

      <div className="cb-card" style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <RangePicker value={periode} onChange={setPeriode} format="DD/MM/YYYY"
          placeholder={[tr('balance.dateDebut'), tr('balance.dateFin')]} allowClear={false} />
        <Button type="primary" icon={<SearchOutlined />} onClick={charger} loading={loading}
          style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
          {tr('balance.calculer')}
        </Button>
        <Input allowClear placeholder={tr('balance.filtrer')} style={{ width: 220 }}
          onChange={(e) => setRecherche(e.target.value)} />
        <div style={{ flex: 1 }} />
        {/* UN SEUL BOUTON, AVEC SES OPTIONS. `TableExportButton` pose lui-meme le coin
            bas droit, la reserve de place en bas de page, l'etat desactive et le libelle
            de chargement - tout ce que les deux boutons poses a la main refaisaient a leur
            facon, et pas tout a fait pareil d'un ecran a l'autre. */}
          <TableExportButton
            floating
            data={lignesAPlat}
            columns={colonnesExport}
            disabled={!balance}
            filename={balance ? `balance_${balance.debut}_${balance.fin}` : 'balance'}
            title={tr('balance.titre')}
            subtitle={tr('balance.export.sousTitre', { periode: libellePeriode })}
            orientation="landscape"
          />
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={lignes}
          columns={colonnes}
          rowKey="numeroCompte"
          /* Les enfants s'appellent `enfants`, pas `children` : sans cela antd
             afficherait l'arbre à plat et le cumul ferait doublon à l'écran. */
          childrenColumnName="enfants"
          expandable={{ defaultExpandAllRows: false, indentSize: 18 }}
          rowClassName={(r) => (r.regroupement ? 'ligne-regroupement' : '')}
          loading={loading}
          bordered
          size="middle"
          scroll={{ x: 1150 }}
          pagination={false}
          locale={{ emptyText: <Empty description={tr('balance.aucuneEcriture')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          summary={() => (t && !recherche ? (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: '#f6faf7', fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Space>
                    {tr('balance.totaux')}
                    {t.equilibree ? (
                      <span style={{ color: GREEN, fontWeight: 600 }}><CheckCircleFilled /> {tr('balance.equilibree')}</span>
                    ) : (
                      <Tooltip title={tr('balance.desequilibreeAide')}>
                        <span style={{ color: ROUGE, fontWeight: 600 }}><WarningFilled /> {tr('balance.desequilibree')}</span>
                      </Tooltip>
                    )}
                  </Space>
                </Table.Summary.Cell>
                {['soldeDebutDebiteur', 'soldeDebutCrediteur', 'mouvementDebit', 'mouvementCredit',
                  'soldeFinDebiteur', 'soldeFinCrediteur'].map((cle, i) => (
                  <Table.Summary.Cell key={cle} index={i + 2} align="right">
                    {formatMontant(t[cle])}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          ) : null)}
        />
      </div>
    </Layout>
  );
}
